/**
 * C.2 — student home, 7c — my result, question by question, and the undrawn student screens
 * (§1.6): My classes and Account (password change).
 *
 * Home follows design p-69: live first when something is live, then classroom mastery as a
 * band and a band per criterion (no percentages on home), coming up, and the teal practice
 * strip, which lives in the practice module so navy and teal never share a file.
 */

import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useSession } from '../../lib/session';
import { useAction, useQuery } from '../../lib/useQuery';
import { api } from '../../lib/api';
import { BAND_COLOR, BandChip, Btn, Chip, DesignShell, Empty, ErrorNote, Loading, S, type Band } from '../../design/kit';
import { PracticeStrip } from '../practice/ui';

/* ------------------------------------------------------------------- C.2 */

interface Live {
  id: string; title: string; kind: string; status: string;
  joinCode?: string; teacher: string; subject: string | null; startedAt: string | null;
}
interface ComingUp { kind: 'work' | 'sitting'; id: string; title: string; sub: string; at: string | null; to: string }
interface Mastery {
  cohortId: string; className: string; subject: string; pieces: number; band: Band; bandLabel: string;
  criteria: { letter: string; name: string | null; band: Band; bandLabel: string }[];
}
interface HomePayload {
  cohorts: { id: string; name: string; grade: string }[];
  live: Live[];
  mastery: Mastery | null;
  comingUp: ComingUp[];
  practice: { streak: number; sessions: number; badges: number } | null;
  pyp?: boolean;
  /* C.5a: the nearest unfinished piece; null when the student's classes owe none. */
  projects: { id: string; label: string; subject: string | null; done: number; total: number; pct: number; next: string | null; dueAt: string; count: number } | null;
  unread: number;
}

function sinceLabel(iso: string | null): string {
  if (!iso) return '';
  const mins = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 1) return 'just started';
  if (mins === 1) return 'started 1 minute ago';
  if (mins < 60) return `started ${mins} minutes ago`;
  return `started ${Math.round(mins / 60)}h ago`;
}

/** "Thu" this week, "27 Mar" after it. */
function dayChip(iso: string | null): string {
  if (!iso) return 'Open';
  const d = new Date(iso);
  const days = (d.getTime() - Date.now()) / 86_400_000;
  return days >= -1 && days < 6 ? d.toLocaleDateString(undefined, { weekday: 'short' }) : d.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
}

/** A band as a bar length, so the card reads at a glance without a single percentage (C.2). */
const BAND_WIDTH: Record<Band, number> = { no_evidence: 0, not_yet: 25, approaching: 50, secure: 75, strong: 100 };

function greeting(): string {
  const h = new Date().getHours();
  return h < 12 ? 'Morning' : h < 18 ? 'Afternoon' : 'Evening';
}

export function StudentHome() {
  const { user, school } = useSession();
  const { data, loading, error, reload } = useQuery<HomePayload>('/student/home');

  const week = data?.comingUp.filter((c) => c.at && new Date(c.at).getTime() - Date.now() < 7 * 86_400_000).length ?? 0;
  const shell = (children: React.ReactNode) => (
    <DesignShell
      brand={school?.name}
      title={`${greeting()}, ${user?.name?.split(' ')[0] ?? 'there'}`}
      meta={data ? <span>{week === 0 ? 'Nothing due this week.' : `${week} thing${week === 1 ? '' : 's'} coming up this week.`}</span> : undefined}
      footer={{ name: user?.name ?? '', sub: data?.cohorts.map((c) => c.name).join(', ') }}
      actions={<Btn kind="secondary" to="/join">Join with a code</Btn>}
    >
      {children}
    </DesignShell>
  );

  if (loading && !data) return shell(<Loading />);
  if (error || !data) return shell(<ErrorNote error={error} retry={reload} />);
  const m = data.mastery;

  return shell(
    <>
      {/* Live first — the only thing happening right now. */}
      {data.live.length > 0 && (
        <>
          <span style={S.sectionLabel}>Live now</span>
          {data.live.map((l) => (
            <div key={l.id} style={{ ...S.card, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '20px', flexWrap: 'wrap' }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: '1.0625rem', fontWeight: 600, letterSpacing: '-.01em' }}>{l.title}</div>
                <div style={S.sub}>{[l.teacher, l.subject, sinceLabel(l.startedAt)].filter(Boolean).join(' · ')}</div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                {l.joinCode && (
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ ...S.mutedXs, marginBottom: '2px' }}>Room code</div>
                    <div style={{ fontFamily: 'ui-monospace,Menlo,monospace', fontSize: '1.25rem', fontWeight: 600, letterSpacing: '.16em' }}>{l.joinCode}</div>
                  </div>
                )}
                <Btn kind="primaryLg" to={`/student/live/${l.id}`}>Join</Btn>
              </div>
            </div>
          ))}
        </>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '14px', alignItems: 'start' }}>
        {/* Classroom mastery: a band and a band per criterion, never a percentage. PYP has none (D-3). */}
        {data.pyp ? (
          <section aria-label="Portfolio" style={S.card}>
            <h2 style={{ ...S.h3, margin: 0 }}>Your portfolio</h2>
            <p style={{ ...S.mutedSm, margin: '8px 0 12px' }}>The work your teacher has collected, with what it shows.</p>
            <Btn kind="secondary" to="/student/portfolio">Open portfolio</Btn>
          </section>
        ) : <section aria-label="Classroom mastery" style={{ ...S.card, borderTop: '3px solid var(--c-band-strong)', gridColumn: 'span 1' }}>
          {m ? (
            <>
              <div style={{ ...S.between, flexWrap: 'wrap' }}>
                <h2 style={{ ...S.h3, margin: 0 }}>Classroom mastery · {m.subject}</h2>
                <Chip tone="brand">Marked by your teacher</Chip>
              </div>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: '10px', margin: '12px 0 14px', flexWrap: 'wrap' }}>
                <span style={{ fontSize: '1.75rem', fontWeight: 650, letterSpacing: '-.02em' }}>{m.bandLabel}</span>
                <span style={S.mutedSm}>from {m.pieces} marked piece{m.pieces === 1 ? '' : 's'}</span>
              </div>
              {m.criteria.length === 0 ? (
                <p style={{ ...S.mutedSm, margin: 0 }}>Criterion bands appear once a rubric question in this class is marked.</p>
              ) : (
                <div style={{ display: 'grid', gap: '11px' }}>
                  {m.criteria.map((c) => (
                    <div key={c.letter}>
                      <div style={{ ...S.between, fontSize: '0.7812rem' }}>
                        <span>{c.letter}{c.name ? ` · ${c.name}` : ''}</span>
                        <span style={{ fontWeight: 600 }}>{c.bandLabel}</span>
                      </div>
                      <div role="img" aria-label={`Criterion ${c.letter}: ${c.bandLabel}`} style={{ height: '6px', borderRadius: '3px', background: 'var(--c-track)', marginTop: '5px' }}>
                        <div style={{ width: `${BAND_WIDTH[c.band]}%`, height: '100%', borderRadius: '3px', background: BAND_COLOR[c.band] }} />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          ) : (
            <Empty title="No marked work yet" body="Your mastery band appears once your teacher marks and returns work." />
          )}
        </section>}

        <div style={{ display: 'grid', gap: '14px' }}>
          <section aria-label="Coming up" style={S.card}>
            <h2 style={{ ...S.h3, margin: '0 0 10px' }}>Coming up</h2>
            {data.comingUp.length === 0 ? (
              <p style={{ ...S.mutedSm, margin: 0 }}>Nothing due. When your teacher sets something, it shows up here.</p>
            ) : (
              <div style={{ display: 'grid', gap: '4px' }}>
                {data.comingUp.slice(0, 6).map((c) => (
                  <Link key={`${c.kind}-${c.id}`} to={c.to} style={{ ...S.between, padding: '7px 0', color: 'inherit', textDecoration: 'none' }}>
                    <span style={{ minWidth: 0 }}>
                      <span style={{ display: 'block', fontSize: '0.8125rem', fontWeight: 600 }}>{c.title}</span>
                      <span style={S.mutedXs}>{c.sub}</span>
                    </span>
                    <Chip tone={c.kind === 'sitting' ? 'brand' : 'warn'}>{dayChip(c.at)}</Chip>
                  </Link>
                ))}
              </div>
            )}
          </section>
          {data.projects && (
            <section aria-label="Project progress" style={{ ...S.card, display: 'grid', gap: '8px' }}>
              <div style={S.between}>
                <span style={S.h3}>{data.projects.label} progress</span>
                <Link to={`/student/projects/${data.projects.id}`} style={S.link}>{data.projects.count > 1 ? `All ${data.projects.count} pieces` : 'Open'}</Link>
              </div>
              <div style={S.between}>
                <span style={S.small}>{data.projects.done} of {data.projects.total} milestones{data.projects.subject ? ` · ${data.projects.subject}` : ''}</span>
                <strong style={S.strong}>{data.projects.pct}%</strong>
              </div>
              <div role="img" aria-label={`${data.projects.done} of ${data.projects.total} milestones`} style={{ height: '8px', borderRadius: '4px', background: 'var(--c-track)' }}>
                <div style={{ width: `${data.projects.pct}%`, height: '100%', borderRadius: '4px', background: 'var(--c-brand)' }} />
              </div>
              {data.projects.next && <div style={S.mutedXs}>Next: {data.projects.next.toLowerCase()} · due {dayChip(data.projects.dueAt)}</div>}
            </section>
          )}
        </div>
      </div>

      {data.practice && <PracticeStrip {...data.practice} />}
    </>,
  );
}

/* ------------------------------------------------------- My classes (§1.6) */

interface ClassRow {
  id: string; name: string; grade: string; subject: string | null; teachers: string[];
  band: Band; bandLabel: string; pieces: number; next: { title: string; at: string } | null;
}

export function StudentClasses() {
  const { user, school } = useSession();
  const { data, loading, error, reload } = useQuery<{ classes: ClassRow[] }>('/student/classes');
  const body = loading && !data ? <Loading /> : error || !data ? <ErrorNote error={error} retry={reload} /> : data.classes.length === 0 ? (
    <div style={S.cardFlush}><Empty title="No classes yet" body="Your school adds you to classes; they appear here once it does." /></div>
  ) : (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '14px' }}>
      {data.classes.map((c) => (
        <section key={c.id} aria-label={c.name} style={S.card}>
          <h2 style={{ ...S.h3, margin: 0 }}>{c.name}</h2>
          <div style={S.sub}>{[c.subject, c.teachers.join(', ')].filter(Boolean).join(' · ')}</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '12px' }}>
            <BandChip band={c.band} label={c.bandLabel} />
            <span style={S.mutedXs}>{c.pieces ? `from ${c.pieces} marked piece${c.pieces === 1 ? '' : 's'}` : 'nothing marked yet'}</span>
          </div>
          <div style={{ ...S.mutedXs, marginTop: '10px' }}>
            {c.next ? `Next: ${c.next.title} · ${new Date(c.next.at).toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' })}` : 'Nothing scheduled'}
          </div>
        </section>
      ))}
    </div>
  );
  return <DesignShell brand={school?.name} title="My classes" footer={{ name: user?.name ?? '' }}>{body}</DesignShell>;
}

/* ------------------------------------------------------- password change (§1.6) */

export function StudentAccount() {
  const { user, school } = useSession();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [done, setDone] = useState(false);
  const save = useAction(async () => {
    await api.post('/auth/me/password', { current, next });
    setCurrent('');
    setNext('');
    setDone(true);
  });
  const field: React.CSSProperties = { minHeight: '40px', borderRadius: '9px', border: '1px solid var(--c-line-strong)', padding: '0 12px', fontSize: '0.875rem', width: '100%' };
  return (
    <DesignShell brand={school?.name} title="Account" footer={{ name: user?.name ?? '' }}>
      <section aria-label="Your details" style={{ ...S.card, maxWidth: '520px' }}>
        <h2 style={{ ...S.h3, margin: 0 }}>{user?.name}</h2>
        <div style={S.sub}>{user?.email}</div>
        <p style={{ ...S.mutedSm, margin: '10px 0 0', lineHeight: 1.6 }}>Your name and classes come from your school's roster. Ask your coordinator if either is wrong.</p>
      </section>
      <form
        aria-label="Change password"
        onSubmit={(e) => { e.preventDefault(); setDone(false); void save.run(); }}
        style={{ ...S.card, maxWidth: '520px', display: 'grid', gap: '12px' }}
      >
        <h2 style={{ ...S.h3, margin: 0 }}>Change password</h2>
        <label style={{ display: 'grid', gap: '5px', fontSize: '0.7812rem', fontWeight: 600 }}>
          Current password
          <input type="password" autoComplete="current-password" required value={current} onChange={(e) => setCurrent(e.target.value)} style={field} />
        </label>
        <label style={{ display: 'grid', gap: '5px', fontSize: '0.7812rem', fontWeight: 600 }}>
          New password
          <input type="password" autoComplete="new-password" required minLength={8} value={next} onChange={(e) => setNext(e.target.value)} style={field} />
          <span style={{ ...S.mutedXs, fontWeight: 400 }}>At least 8 characters.</span>
        </label>
        {save.error ? <ErrorNote error={save.error} /> : null}
        {done ? <div role="status" style={{ ...S.small, color: 'var(--c-good)' }}>Password changed.</div> : null}
        <div><Btn kind="primary" type="submit" disabled={save.pending}>{save.pending ? 'Saving…' : 'Change password'}</Btn></div>
      </form>
    </DesignShell>
  );
}

/* ------------------------------------------------------------------- 7c */

interface QuestionRow {
  questionId: string; stem: string; type?: string; options: string[]; yourAnswer: string[];
  mark: number; marks: number; band?: Band; skills: string[]; commandTerm: string | null;
  outcome: 'full' | 'partial' | 'missed' | 'none'; feedback?: string; correctAnswer?: string[] | null;
  responseId?: string | null; query?: 'open' | 'escalated' | 'resolved' | null;
}

interface ResultPayload {
  result: {
    id: string; title: string; kind: string; percentage: number | null;
    band: Band; bandLabel: string; total?: number; max?: number;
    teacherComment?: string; classAverage?: number; releasedAt?: string;
  };
  questions: QuestionRow[];
}

/** The design's three-state marker: full marks, partial, missed. */
const MARKER: Record<QuestionRow['outcome'], { glyph: string; color: string; bg: string; label: string }> = {
  full: { glyph: '✓', color: 'var(--c-good)', bg: 'var(--c-good-wash)', label: 'Full marks' },
  partial: { glyph: '~', color: 'var(--c-warn-ink)', bg: 'var(--c-warn-wash)', label: 'Partly right' },
  missed: { glyph: '✗', color: 'var(--c-bad)', bg: 'var(--c-bad-wash)', label: 'Not yet' },
  none: { glyph: '·', color: 'var(--c-ink-3)', bg: 'var(--c-surface-3)', label: 'Not marked' },
};

export function StudentResult() {
  const { id = '' } = useParams();
  const { user, school } = useSession();
  const navigate = useNavigate();
  const { data, loading, error, reload } = useQuery<ResultPayload>(`/student/results/${id}`);

  const shell = (children: React.ReactNode) => (
    <DesignShell
      brand={school?.name}
      footer={{ name: user?.name ?? '' }}
      title={
        <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <button type="button" onClick={() => navigate('/student/results')} style={{ ...S.link, border: 'none', background: 'none', fontSize: '1.1875rem', fontWeight: 600 }}>
            My assessments
          </button>
          <span style={S.muted} aria-hidden="true">›</span>
          <span>{data?.result.title ?? ''}</span>
        </span>
      }
    >
      {children}
    </DesignShell>
  );

  if (loading && !data) return shell(<Loading />);
  if (error || !data) return shell(<ErrorNote error={error} retry={reload} />);
  const { result } = data;

  return shell(
    <>
      <div style={S.mutedSm}>
        {[
          result.kind === 'assessment' ? 'e-Assessment' : 'Live event',
          result.releasedAt ? new Date(result.releasedAt).toLocaleDateString(undefined, { day: 'numeric', month: 'long' }) : null,
          'returned',
        ].filter(Boolean).join(' · ')}
      </div>

      {/* Marks and band side by side, never reconciled into one number. */}
      <div style={{ display: 'flex', gap: '14px', flexWrap: 'wrap' }}>
        {result.total !== undefined && (
          <div style={{ ...S.card, minWidth: '180px' }}>
            <div style={S.sectionLabel}>Marks</div>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: '4px', marginTop: '8px' }}>
              <span style={{ fontSize: '2rem', fontWeight: 600, letterSpacing: '-.02em' }}>{result.total}</span>
              <span style={{ ...S.mutedSm, fontSize: '0.9375rem' }}>/{result.max}</span>
            </div>
            {result.classAverage !== undefined && (
              <div style={{ ...S.mutedXs, marginTop: '6px' }}>Class average {result.classAverage}%</div>
            )}
          </div>
        )}
        <div style={{ ...S.card, minWidth: '180px' }}>
          <div style={S.sectionLabel}>Band</div>
          <div style={{ marginTop: '10px' }}><BandChip band={result.band} label={result.bandLabel} /></div>
        </div>
      </div>

      {result.teacherComment && (
        <div style={{ ...S.card, background: 'var(--c-brand-wash)', border: '1px solid var(--c-brand-line)' }}>
          <div style={S.sectionLabel}>From your teacher</div>
          <p style={{ fontSize: '0.8125rem', lineHeight: 1.65, margin: '8px 0 0' }}>{result.teacherComment}</p>
        </div>
      )}

      {data.questions.length === 0 ? (
        <div style={S.cardFlush}>
          <Empty title="Just the band this time" body="Your teacher chose to release the overall picture rather than question by question." />
        </div>
      ) : (
        <>
          <span style={{ ...S.sectionLabel, marginTop: '8px' }}>Question by question</span>
          <div style={S.cardFlush}>
            {data.questions.map((q, i) => {
              const m = MARKER[q.outcome];
              return (
                <div
                  key={q.questionId}
                  style={{ display: 'flex', gap: '14px', padding: '16px 20px', borderTop: i === 0 ? 'none' : '1px solid var(--c-line-soft)' }}
                >
                  <span
                    aria-label={m.label}
                    title={m.label}
                    style={{
                      width: '26px', height: '26px', flex: 'none', borderRadius: '50%',
                      background: m.bg, color: m.color, fontSize: '0.75rem', fontWeight: 600,
                      display: 'grid', placeItems: 'center',
                    }}
                  >
                    {m.glyph}
                  </span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: '0.8125rem', fontWeight: 500, lineHeight: 1.5 }}>{q.stem}</div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '7px', flexWrap: 'wrap' }}>
                      <span style={{ fontSize: '0.7812rem', fontWeight: 600 }}>{q.mark}/{q.marks}</span>
                      {q.skills.map((s) => <Chip key={s} tone="plain">{s}</Chip>)}
                      {q.commandTerm ? <Chip tone="plain">{q.commandTerm}</Chip> : null}
                    </div>
                    {q.yourAnswer.length > 0 && (
                      <div style={{ ...S.mutedSm, marginTop: '7px', lineHeight: 1.55 }}>
                        You answered:{' '}
                        {q.options.length > 0
                          ? q.yourAnswer.map((a) => q.options[Number(a)] ?? a).join(', ')
                          : q.yourAnswer.join(' ')}
                      </div>
                    )}
                    {q.correctAnswer && (
                      <div style={{ fontSize: '0.75rem', color: 'var(--c-good)', marginTop: '4px' }}>
                        Answer:{' '}
                        {q.options.length > 0
                          ? q.correctAnswer.map((a) => q.options[Number(a)] ?? a).join(', ')
                          : q.correctAnswer.join(', ')}
                      </div>
                    )}
                    {q.feedback && (
                      <div style={{ fontSize: '0.7812rem', color: 'var(--c-ink-2)', marginTop: '7px', lineHeight: 1.55 }}>{q.feedback}</div>
                    )}
                    {q.responseId && q.outcome !== 'none' ? <QueryMark responseId={q.responseId} state={q.query ?? null} onDone={reload} /> : null}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      <div style={{ display: 'flex', gap: '9px', marginTop: '6px' }}>
        <Btn kind="secondary" to="/student/performance">See where this sits</Btn>
        <Btn kind="secondary" to="/student/results">All my results</Btn>
      </div>
    </>,
  );
}

/** B-5: a student may query a mark. It goes to the teacher, then the coordinator resolves it. */
function QueryMark({ responseId, state, onDone }: { responseId: string; state: 'open' | 'escalated' | 'resolved' | null; onDone: () => void }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const send = useAction(async () => {
    await api.post(`/moderation/responses/${responseId}/query`, { reason });
    setOpen(false);
    onDone();
  });
  if (state === 'open' || state === 'escalated') return <div style={{ ...S.mutedXs, marginTop: '7px' }}>You queried this mark — it is with the school.</div>;
  if (state === 'resolved') return <div style={{ ...S.mutedXs, marginTop: '7px' }}>Your query on this mark was resolved.</div>;
  return open ? (
    <div style={{ display: 'grid', gap: '6px', marginTop: '8px' }}>
      <textarea aria-label="Why you think the mark is wrong" rows={2} value={reason} onChange={(e) => setReason(e.target.value)} style={{ width: '100%' }} />
      <div style={{ display: 'flex', gap: '8px' }}>
        <Btn kind="primarySm" disabled={reason.trim().length < 3 || send.pending} onClick={() => send.run()}>Send query</Btn>
        <Btn kind="secondarySm" onClick={() => setOpen(false)}>Cancel</Btn>
      </div>
      {send.error ? <ErrorNote error={send.error} /> : null}
    </div>
  ) : (
    <button type="button" onClick={() => setOpen(true)} style={{ ...S.link, border: 'none', background: 'none', padding: 0, marginTop: '7px', fontSize: '0.75rem' }}>Query this mark</button>
  );
}
